"use client";

import React, {ReactElement} from "react";
import {StyledBoxColumn, StyledContainer} from "@/shared/StyledComponents";
import {Button, Card, Grid, useTheme} from "@mui/material";
import {PD_LG, PD_XSM, SZ_MD, SZ_XLG} from "@/shared/dimens";
import {AddOutlined} from "@mui/icons-material";
import {BaseApp} from "@/shared/BaseApp";
import {Action} from "@/shared/types";
import {StoredDataFileUploadFragment} from "@/shared/StoredDateFileUploadFragment";
import {ActionsDialogContent} from "@/shared/Dialogs";
import {DesktopFile} from "@/shared/files";
import {MimeTypeMap} from "@/shared/mime_types";
import {colorRed} from "@/shared/colors";
import {COLOR_SECONDARY} from "../../app/App";

export type GalleryUploadFile = {
  url: string,
  thumbnailUrl?: string,
  mimeType?: string,
}

export type GalleryUploadChange = {
  index: number,
  file: DesktopFile,
}

export class GalleryUpload extends React.Component<{
  readonly?: boolean,
  hideUpload?: boolean,
  galleryId: string,
  value: GalleryUploadFile[],
  mimeTypes: string[],
  storedDataSourceBasePath: string,
  setValue: (value: GalleryUploadFile[], change: GalleryUploadChange) => void,
  showGalleryDialog: (files: GalleryUploadFile[], selectedIndex?: number) => void,
}> {
  private onChangeFile(value: GalleryUploadFile, setValue: (url: string, file?: DesktopFile) => void) {
    BaseApp.CONTEXT.showDialog(null, () => this.showChangeFileOptions(value, setValue))
  }

  private showChangeFileOptions(value: GalleryUploadFile, setValue: (url: string, file?: DesktopFile) => void) {
    const actions: Action[] = [];
    if (!value) {
      actions.push(...[
        new Action("Upload", () => {
          BaseApp.CONTEXT.showDialog(null, () => <StoredDataFileUploadFragment
            isDialog={true}
            mimeTypes={this.props.mimeTypes}
            storedDataSource={`${this.props.storedDataSourceBasePath}/${this.props.galleryId}`}
            onSelectionChanged={(urls: string[], files?: DesktopFile[]) => {
              setValue(urls[0], files?.[0]);
            }}/>);
        }),
      ]);
    }
    if (value) {
      actions.push(...[
        new Action("View", () => {
          this.props.showGalleryDialog([value]);
        }),
        new Action("Remove", () => {
          setValue(null);
        }),
        new Action("Copy URL", () => {
          navigator.clipboard.writeText(value.url);
          BaseApp.CONTEXT.showToast("Copied.");
        }),
      ]);
    }
    return <ActionsDialogContent actions={actions}/>;
  }

  private toUploadFile(url: string, file: DesktopFile): GalleryUploadFile {
    const name = file?.file?.name || "File";
    const n = name?.lastIndexOf(".");
    const mimeType = file?.mimeType || MimeTypeMap.getInstance().getMimeTypeFromExtension(n >= 0 ? name.substring(n + 1) : null);
    return {url, mimeType: mimeType};
  }

  private renderThumbnail(file: GalleryUploadFile): ReactElement {
    if (MimeTypeMap.isImage(file.mimeType)) {
      return <img src={file.url} style={{width: "100%", height: "100%", objectFit: "cover"}}/>;
    }
    const IconType = MimeTypeMap.getIconForMimeType(file.mimeType);
    return <IconType style={{color: COLOR_SECONDARY, width: SZ_XLG, height: SZ_XLG}}/>;
  }

  render(): ReactElement {
    const uploadFiles = [...(this.props.value || [])];
    const allFiles = [...uploadFiles];
    if (!this.props.readonly && !this.props.hideUpload) {
      allFiles.push({url: "_upload"});
    }
    return <StyledContainer size={"xlg"} style={{padding: PD_XSM}}>
      <Grid container>
        {allFiles.map((uploadFile, index) => {
          return <Grid xs={6} sm={4}>
            <StyledBoxColumn style={{width: "100%", alignItems: "center", padding: PD_LG}}>
              <Card style={{width: '100%', height: '100%', aspectRatio: 1}}>
                <Button
                  onClick={() =>
                    uploadFile.url !== "_upload"
                      ? this.onChangeFile(uploadFile, (url, file: DesktopFile) => {
                        uploadFiles[index] = this.toUploadFile(url, file);
                        this.props.setValue(uploadFiles, {index: index, file: file});
                      })
                      : this.onChangeFile(null, (url, file: DesktopFile) => {
                        uploadFiles.push(this.toUploadFile(url, file));
                        this.props.setValue(uploadFiles, {index: uploadFiles.length - 1, file: file});
                      })}
                  style={{
                    padding: 0,
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}>
                  {uploadFile.url !== "_upload"
                    ? this.renderThumbnail(uploadFile)
                    : <AddOutlined style={{width: SZ_MD, height: SZ_MD}}/>}
                </Button>
              </Card>
            </StyledBoxColumn>
          </Grid>;
        })}
      </Grid>
    </StyledContainer>;
  }
}